import { useEffect, useState } from "react";
import ScheduledGigsWorkspace, { type ScheduledGigCard } from "./ScheduledGigsWorkspace";
import { reviewsApi } from "../../lib/reviews";
import { teenScheduledGigsApi } from "../../lib/scheduledGigs";
import type { ScheduledGigItemResponse } from "../../types/scheduledGigTypes";
import { apiErrorToMessage } from "../../utils/ApiUtils";

const normalizeGig = (gig: ScheduledGigItemResponse): ScheduledGigCard => ({
  requestId: gig.requestId,
  gigId: gig.gigId,
  gigTitle: gig.gigTitle,
  gigDescription: gig.gigDescription,
  gigRequiresEquipment: gig.gigRequiresEquipment,
  gigEquipmentDescription: gig.gigEquipmentDescription,
  locationType: gig.locationType,
  city: gig.city,
  state: gig.state,
  zip: gig.zip,
  gigTimeZone: gig.gigTimeZone,
  participantName: [gig.participantFirstName, gig.participantLastName].filter(Boolean).join(" ") || "Adult",
  participantLabel: "Adult contact",
  paymentAmountCents: gig.paymentAmountCents,
  volunteer: gig.volunteer,
  teensRequired: Math.max(1, gig.teensRequired ?? 1),
  coveredSeats: Math.max(1, gig.coveredSeats ?? 1),
  confirmedStartTime: gig.confirmedStartTime,
  confirmedEndTime: gig.confirmedEndTime,
  scheduleProposals: gig.scheduleProposals ?? [],
  teenArrivedAt: gig.teenArrivedAt,
  teenMarkedCompletedAt: gig.teenMarkedCompletedAt,
  adultConfirmedCompletedAt: gig.adultConfirmedCompletedAt,
  issueReportedAt: gig.issueReportedAt,
  issueReportMessage: gig.issueReportMessage,
  paymentStatus: gig.paymentStatus,
  fundedAt: gig.fundedAt,
  releasedAt: gig.releasedAt,
  refundedAt: gig.refundedAt,
});

const TeenScheduledGigsWorkspace = () => {
  const [gigs, setGigs] = useState<ScheduledGigItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadGigs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      setGigs(await teenScheduledGigsApi.list());
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to load your scheduled gigs."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadGigs();
  }, []);

  return (
    <ScheduledGigsWorkspace
      role="teen"
      title="Scheduled Gigs"
      subtitle="Track the gigs you have already been matched to and mark them complete when you are done. The gig is completed once both sides have marked it complete."
      loadingMessage="Loading your scheduled gigs..."
      emptyMessage="Once a gig is confirmed by both the teen and the adult, it will appear here."
      gigs={gigs.map(normalizeGig)}
      isLoading={isLoading}
      error={error}
      onTeenMarkArrived={async (requestId) => {
        await teenScheduledGigsApi.markArrived(requestId);
        await loadGigs();
      }}
      onTeenMarkComplete={async (requestId) => {
        await teenScheduledGigsApi.markComplete(requestId);
        await loadGigs();
      }}
      onTeenSubmitReview={async (requestId, payload) => {
        await reviewsApi.create(requestId, payload);
      }}
      onReportIssue={async (requestId, message) => {
        await teenScheduledGigsApi.reportIssue(requestId, { message });
        await loadGigs();
      }}
      onCancel={async (requestId, note) => {
        await teenScheduledGigsApi.cancel(requestId, note ? { note } : undefined);
        await loadGigs();
      }}
      onLeaveGig={async (requestId, note) => {
        await teenScheduledGigsApi.leave(requestId, { note });
        await loadGigs();
      }}
    />
  );
};

export default TeenScheduledGigsWorkspace;
